import { Typography } from '@mui/material';
import { Grid, Button, Box, Skeleton, Stack, TextField, FormHelperText } from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from '../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { useEffect, useState } from 'react';

import { setDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  voicePromptVolume: {
    volume_level: 0,
    submit: null
  }
};

const VoicePromptVolumeSettingsPage = () => {
  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [voicePromptVolumeSettings, setVoicePromptVolumeSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 15 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (!selectedDevice) return;
    setLoading(true);
    (async () => {
      await FirestoreDataService.getDeviceSettings(selectedDevice)
        .then((documentSnap) => {
          if (documentSnap.exists()) {
            let voicePromptVolume = documentSnap.data().voice_prompt_volume;
            // console.log('Fetched:-', voicePromptVolume);
            let voicePromptVolumeObject = { volume_level: voicePromptVolume };
            setVoicePromptVolumeSettings(voicePromptVolumeObject);
          }
        })
        .catch((e) => {
          const msg = 'Failed to get voice prompt volume settings!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
          console.info(msg, e);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [selectedDevice]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary">
          Voice Prompt Volume Settings
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" height={230} />
          </>
        ) : (
          <Formik
            initialValues={voicePromptVolumeSettings ? voicePromptVolumeSettings : INITIAL_VALUES}
            validationSchema={Yup.object().shape({
              voicePromptVolume: Yup.object()
                .shape({
                  volume_level: Yup.number()
                })
                .required()
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                // console.log(values);
                setStatus({ success: false });
                setSubmitting(true);
                let payload = {};

                // Check permission: Try Redux appUser first, fallback to sessionStorage for partner sessions
                let canModifySettings = appUser?.organization?.extra_info?.can_user_modify_settings;
                if (!canModifySettings) {
                  const isPartnerSession = typeof window !== 'undefined' && sessionStorage.getItem('is_partner_session') === 'true';
                  if (isPartnerSession) {
                    try {
                      const userDataStr = sessionStorage.getItem('user');
                      if (userDataStr) {
                        const partnerUser = JSON.parse(userDataStr);
                        canModifySettings = partnerUser?.organization?.extra_info?.can_user_modify_settings;
                      }
                    } catch (e) {
                      console.error('Failed to parse partner user data from sessionStorage:', e);
                    }
                  }
                }
                if (!canModifySettings) {
                  console.log('cant save settings');
                  dispatch(
                    showAppAlert({
                      show: true,
                      alertType: 'error',
                      alertMessage: 'Error: You do not have permission to save these settings.'
                    })
                  );
                  return;
                }

                if (!values.volume_level && values.volume_level !== 0) {
                  setErrors({ submit: 'Volume Level Required!' });
                  return;
                } else {
                  const volumeLevel = parseInt(values.volume_level);

                  if (isNaN(volumeLevel) || volumeLevel < 0 || volumeLevel > 100) {
                    setErrors({ submit: 'Volume level should be between 0 and 100.' });
                    return;
                  } else {
                    payload = { volume_level: volumeLevel };
                    // console.log('payload:', payload);
                  }
                }

                const tasksRef = doc(db, 'tasks', selectedDevice);
                setDoc(tasksRef, {
                  device_id: selectedDevice,
                  type: 'voice_prompt_volume',
                  user_id: appUser.email ? appUser.email : '-',
                  old_value: voicePromptVolumeSettings,
                  payload
                })
                  .then((response) => {
                    console.log('saved tasks!', response);
                    const msg = 'Voice prompt volume settings saved successfully !';
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    console.log('e', e);
                    const msg = 'Failed to save voice prompt volume settings!';
                    console.log(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  });
              } catch (err) {
                console.log('err', err);
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save Voice Prompt Volume Settings!' }));
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    {/* <Typography variant="caption" color="textSecondary">
                      {JSON.stringify(values)}
                    </Typography> */}
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Stack spacing={1} style={{ marginTop: '16px' }}>
                        <TextField
                          fullWidth
                          id="volume_level"
                          type="number"
                          value={values.volume_level}
                          name="volume_level"
                          onChange={handleChange}
                          placeholder="Enter Volume Level (0 - 100)"
                          label="Volume Level"
                          required
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} lg={12} style={{ paddingTop: 16, paddingBottom: 16, marginBottom: 16 }}>
                      {errors.submit && (
                        <FormHelperText sx={{ pr: 1 }} error>
                          {errors.submit}
                        </FormHelperText>
                      )}
                      <Button style={{ marginLeft: 16, float: 'right' }} type="submit" variant="contained" color="primary">
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        )}
      </MainCard>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </Grid>
  );
};

export default VoicePromptVolumeSettingsPage;
